import {
    Bookmark,
    CollaboratorPermissionType,
    Investment,
    Subteam,
    SubteamMember,
    Team,
    Teammate,
    TeammatePermissionType,
    TeamResourceAccess,
    TeamResourceAccessPrincipalType,
    TeamResourceAccessResourceType,
    User,
} from '@/types';

import { apiClient } from '../http';
import { favoriteService } from './favorites';
import { toTeamCreateInput, toTeamUpdateInput } from './resourceMutationPayloads';
import { createSanitizedResourceService } from './resources';

export type TeamInviteSearchResponse = {
    investments: Investment[];
    users: User[];
};

export type InviteWorkspaceMemberToTeamInput = {
    inviteeId: string;
    permission: TeammatePermissionType;
};

export type CreateSubteamInput = {
    name: string;
    description?: string;
    emoji?: string;
};

export type AddSubteamMemberInput = {
    teammateId: string;
    permission?: TeammatePermissionType;
};

export type GrantTeamResourceAccessInput = {
    teamId?: string;
    subteamId?: string;
    principalType?: TeamResourceAccessPrincipalType;
    resourceType: TeamResourceAccessResourceType;
    resourceId: string;
    permission: CollaboratorPermissionType;
};

const base = createSanitizedResourceService<
    Team,
    ReturnType<typeof toTeamCreateInput>,
    ReturnType<typeof toTeamUpdateInput>
>('/api/teams', toTeamCreateInput, toTeamUpdateInput);

export const teamService = {
    ...base,
    getBookmarks: (teamId: string) => apiClient.get<Bookmark[]>(`/api/teams/${teamId}/bookmarks`),
    getCollaborators: (teamId: string) => apiClient.get<Teammate[]>(`/api/teams/${teamId}/collaborators`),
    getSubteams: (teamId: string) => apiClient.get<Subteam[]>(`/api/teams/${teamId}/subteams`),
    createSubteam: (teamId: string, input: CreateSubteamInput) =>
        apiClient.post<Subteam, CreateSubteamInput>(`/api/teams/${teamId}/subteams`, input),
    updateSubteam: (teamId: string, subteamId: string, input: Partial<CreateSubteamInput & { archived: boolean }>) =>
        apiClient.put<Subteam, Partial<CreateSubteamInput & { archived: boolean }>>(
            `/api/teams/${teamId}/subteams/${subteamId}`,
            input,
        ),
    deleteSubteam: (teamId: string, subteamId: string) =>
        apiClient.delete<void>(`/api/teams/${teamId}/subteams/${subteamId}`),
    addSubteamMember: (teamId: string, subteamId: string, input: AddSubteamMemberInput) =>
        apiClient.post<SubteamMember, AddSubteamMemberInput>(
            `/api/teams/${teamId}/subteams/${subteamId}/members`,
            input,
        ),
    removeSubteamMember: (teamId: string, subteamId: string, subteamMemberId: string) =>
        apiClient.delete<void>(`/api/teams/${teamId}/subteams/${subteamId}/members/${subteamMemberId}`),
    getResourceAccess: (teamId: string) => apiClient.get<TeamResourceAccess[]>(`/api/teams/${teamId}/resource-access`),
    grantResourceAccess: (teamId: string, input: GrantTeamResourceAccessInput) =>
        apiClient.post<TeamResourceAccess, GrantTeamResourceAccessInput>(`/api/teams/${teamId}/resource-access`, input),
    grantSubteamResourceAccess: (teamId: string, subteamId: string, input: GrantTeamResourceAccessInput) =>
        apiClient.post<TeamResourceAccess, GrantTeamResourceAccessInput>(
            `/api/teams/${teamId}/subteams/${subteamId}/resource-access`,
            input,
        ),
    grantWorkspaceResourceAccessToTeam: (input: GrantTeamResourceAccessInput) =>
        apiClient.post<TeamResourceAccess, GrantTeamResourceAccessInput>('/api/teams/resource-access', input),
    joinWorkspaceVisibleTeam: (teamId: string) =>
        apiClient.post<Teammate, Record<string, never>>(`/api/teams/${teamId}/join`, {}),
    revokeResourceAccess: (teamId: string, accessId: string) =>
        apiClient.delete<void>(`/api/teams/${teamId}/resource-access/${accessId}`),
    inviteWorkspaceMember: (teamId: string, input: InviteWorkspaceMemberToTeamInput) =>
        apiClient.post<Teammate, InviteWorkspaceMemberToTeamInput>(`/api/teams/${teamId}/collaborators`, input),
    removeCollaborator: (teamId: string, collaboratorId: string) =>
        apiClient.delete<void>(`/api/teams/${teamId}/collaborators/${collaboratorId}`),
    searchInviteCandidates: (query: string) =>
        apiClient.post<TeamInviteSearchResponse, { query: string }>('/api/search/database/teams/invite', { query }),
    searchTeams: (value: string) => apiClient.post<Team[], { value: string }>('/api/teams/search', { value }),
    setArchived: (id: string, archived: boolean) => base.update(id, { archived }),
    setFavorite: (id: string, favorited: boolean) => favoriteService.set<Team>('teams', id, favorited),
    setLocked: (id: string, locked: boolean) => base.update(id, { locked }),
    updateCollaborator: (teamId: string, collaboratorId: string, updates: Partial<Teammate>) =>
        apiClient.put<Teammate, Partial<Teammate>>(`/api/teams/${teamId}/collaborators/${collaboratorId}`, updates),
};
